<template>
    <section class="med-remuneration pdlr-3" aria-label="Rémunération du médecin">
        <p v-if="loading" role="status">Chargement de la rémunération…</p>
        <div v-if="error" class="text-danger mb-3" role="alert">
            {{ error }}
            <button v-if="!loaded" type="button" class="btn btn-secondary-light-o ml-2" @click="load" :disabled="loading">Réessayer</button>
        </div>
        <p v-if="success" class="text-success" role="status">Rémunération enregistrée.</p>

        <form v-if="loaded" @submit.prevent="save">
            <fieldset :disabled="saving">
                <div class="row align-items-center">
                    <div class="form-group col-md-6 pdl-x">
                        <label for="med-statut">Statut du médecin</label>
                        <select id="med-statut" v-model="profil.statut" class="input-secondary-light" @change="success = false">
                            <option value="interne">Médecin interne</option>
                            <option value="externe">Médecin externe</option>
                        </select>
                    </div>
                    <div class="form-group col-md-6 pdr-x" v-if="!profil.is_active">
                        <p class="text-warning mb-2" role="status">La rémunération de ce médecin est désactivée.</p>
                        <button type="button" class="btn btn-secondary-light-o" @click="reactivateProfile">Réactiver la rémunération</button>
                    </div>
                </div>

                <div v-if="legacyMode" class="med-legacy-notice" role="status">
                    <div>
                        <h3 class="c-title mb-1">Règles historiques par prestation</h3>
                        <p class="mb-0">Cette configuration reste lisible et modifiable comme avant. Les nouveaux modes par catégorie ne modifieront jamais les calculs déjà enregistrés.</p>
                    </div>
                    <div class="med-legacy-actions">
                        <button type="button" class="btn btn-outline-primary" :disabled="saving || !!draft" @click="editRule(-1)">+ Ajouter une règle</button>
                        <button type="button" class="btn btn-primary" :disabled="saving || !!draft" @click="startV3Migration">Créer une configuration V3</button>
                    </div>
                </div>

                <template v-if="legacyMode">
                    <div class="table-responsive">
                        <table class="table med-rules-table">
                            <thead><tr><th>Prestation</th><th>P.U</th><th>Taux</th><th>Base de rémunération</th><th>Rémunération par acte</th><th>État</th><th>Actions</th></tr></thead>
                            <tbody>
                                <tr v-for="(regle, index) in profil.regles" :key="regle.id || 'legacy-' + index">
                                    <td>{{ regle.prestation_libelle }}</td>
                                    <td>
                                        {{ money(regle.montant_prestation) }} {{ devise }}
                                        <span v-if="regle.montant_actuel !== regle.montant_prestation" class="d-block small text-warning">Tarif jour actuel : {{ money(regle.montant_actuel) }}{{ regle.montant_actuel !== null ? ' ' + devise : '' }}</span>
                                    </td>
                                    <td>{{ formatNumberCompact(regle.pourcentage) }} %</td>
                                    <td>{{ money(regle.base_remuneration) }} {{ devise }}</td>
                                    <td>{{ money(remuneration(regle.base_remuneration, regle.pourcentage)) }} {{ devise }}</td>
                                    <td>{{ regle.is_active ? 'Active' : 'Inactive' }}</td>
                                    <td class="med-rule-actions">
                                        <b-dropdown variant="link" right no-caret boundary="viewport" toggle-class="text-secondary px-2" :disabled="saving || !!draft">
                                            <template #button-content>
                                                <i class="fa fa-ellipsis-v" aria-hidden="true"></i>
                                                <span class="sr-only">Actions pour la règle {{ regle.prestation_libelle }}</span>
                                            </template>
                                            <b-dropdown-item-button :disabled="saving || !!draft" @click="editRule(index)">Modifier</b-dropdown-item-button>
                                            <b-dropdown-item-button :disabled="saving || !!draft" @click="toggleRule(index)">{{ regle.is_active ? 'Désactiver' : 'Réactiver' }}</b-dropdown-item-button>
                                            <b-dropdown-item-button :disabled="saving || !!draft" @click="removeRule(index)">Supprimer</b-dropdown-item-button>
                                        </b-dropdown>
                                    </td>
                                </tr>
                                <tr v-if="!profil.regles.length"><td colspan="7" class="text-center text-grey">Aucune règle de rémunération configurée.</td></tr>
                            </tbody>
                        </table>
                    </div>
                </template>

                <template v-else>
                    <div class="med-rules-head">
                        <h3 class="c-title mb-0">Règles</h3>
                        <button type="button" class="btn btn-primary brder-radius" @click="editRule(-1)" :disabled="!!draft || !canAddV3Rule">+ Ajouter une règle</button>
                    </div>
                    <div v-if="migrationIntent" class="alert alert-info med-migration-note d-flex flex-wrap align-items-center justify-content-between">
                        <span>Nouvelle configuration, appliquée après enregistrement.</span>
                        <button type="button" class="btn btn-link p-0 ml-2" @click="cancelV3Migration">Conserver la configuration V1</button>
                    </div>
                    <!-- Quatre colonnes : le tableau tient dans la fiche, sans défilement horizontal. -->
                    <table class="table med-rules-table med-rules-v3-table">
                        <thead><tr><th>Prestation</th><th>Règle</th><th class="text-right">Par acte</th><th class="med-rule-actions"><span class="sr-only">Actions</span></th></tr></thead>
                        <tbody>
                                <tr v-for="(regle, index) in profil.regles" :key="regle.id || 'v3-' + index" :class="{ 'med-rule-inactive': !regle.is_active }">
                                    <td>
                                        <strong>{{ ruleTypeLabel(regle) }}</strong>
                                        <span v-if="!regle.is_active" class="badge badge-secondary ml-1">Inactive</span>
                                        <span class="d-block small text-grey">{{ ruleCategoryLabel(regle) }}<template v-if="ruleMontantPrestation(regle) !== null"> · {{ money(ruleMontantPrestation(regle)) }} {{ devise }}</template></span>
                                    </td>
                                    <td>{{ ruleSummary(regle) }}</td>
                                    <td class="text-right med-rule-due">{{ ruleDueLabel(regle) }}</td>
                                    <td class="med-rule-actions">
                                        <b-dropdown variant="link" right no-caret boundary="viewport" toggle-class="text-secondary px-2" :disabled="saving || !!draft">
                                            <template #button-content>
                                                <i class="fa fa-ellipsis-v" aria-hidden="true"></i>
                                                <span class="sr-only">Actions pour la règle {{ ruleCategoryLabel(regle) }} {{ ruleTypeLabel(regle) }}</span>
                                            </template>
                                            <b-dropdown-item-button :disabled="saving || !!draft" @click="editRule(index)">Modifier</b-dropdown-item-button>
                                            <b-dropdown-item-button :disabled="saving || !!draft" @click="toggleRule(index)">{{ regle.is_active ? 'Désactiver' : 'Réactiver' }}</b-dropdown-item-button>
                                            <b-dropdown-item-button :disabled="saving || !!draft" @click="removeRule(index)">Supprimer</b-dropdown-item-button>
                                        </b-dropdown>
                                    </td>
                                </tr>
                                <tr v-if="!profil.regles.length"><td colspan="4" class="text-center text-grey">Aucune règle.</td></tr>
                        </tbody>
                    </table>
                </template>

                <div v-if="draft" class="med-rule-overlay" @click.stop @mousedown.stop>
                    <div ref="ruleDialog" class="med-rule-editor" role="dialog" aria-modal="true" aria-labelledby="med-rule-title" tabindex="-1">
                        <div class="d-flex align-items-center justify-content-between mb-3">
                            <h4 id="med-rule-title" class="c-title mb-0">{{ isNewRule ? 'Ajouter une règle' : 'Modifier la règle' }}</h4>
                            <button type="button" class="close" aria-label="Fermer la configuration de la règle" @click="closeRule">&times;</button>
                        </div>

                        <div v-if="draftSchema === 1" class="row">
                            <div class="form-group col-md-6">
                                <label for="med-prestation">Prestation</label>
                                <select id="med-prestation" v-model="draft.prestation_id" class="input-secondary-light" @change="selectLegacyPrestation">
                                    <option value="">Choisir une consultation</option>
                                    <option v-for="prestation in legacyAvailablePrestations" :key="prestation.id" :value="prestation.id">{{ prestation.libelle }}</option>
                                </select>
                            </div>
                            <div class="form-group col-md-6">
                                <label for="med-montant">P.U ({{ devise }})</label>
                                <input id="med-montant" class="input-secondary-light" readonly :value="selectedLegacyPrestation ? money(selectedLegacyPrestation.montant) : '—'">
                            </div>
                            <div class="form-group col-md-6">
                                <label for="med-taux">Taux (%)</label>
                                <input id="med-taux" type="number" min="0" max="100" step="0.01" v-model="draft.pourcentage" class="input-secondary-light">
                            </div>
                            <div class="form-group col-md-6">
                                <label for="med-base">Base de rémunération ({{ devise }})</label>
                                <input id="med-base" type="number" min="0" :max="selectedLegacyPrestation ? selectedLegacyPrestation.montant : 0" step="1" v-model="draft.base_remuneration" class="input-secondary-light">
                            </div>
                        </div>

                        <div v-else class="row">
                            <div class="form-group col-md-6">
                                <label for="med-categorie">Catégorie</label>
                                <select id="med-categorie" :value="draft.categorie" class="input-secondary-light" @change="chooseCategory(draft, $event.target.value)">
                                    <option value="" disabled>Choisir une catégorie</option>
                                    <option v-for="categorie in catalogueCategories" :key="categorie.categorie" :value="categorie.categorie" :disabled="categoryUnavailable(categorie, draft)">{{ categorie.libelle }}</option>
                                </select>
                            </div>
                            <div class="form-group col-md-6">
                                <label for="med-type">Type de prestation</label>
                                <!-- Liste longue : champ de recherche (sans tenir compte des accents ni des majuscules). -->
                                <multiselect id="med-type" class="med-type-recherche" :value="typeChoisi(draft)" :options="typesRecherches(draft)"
                                    label="libelle" track-by="cle" :show-labels="false" :allow-empty="false" :internal-search="false"
                                    :disabled="!draft.categorie" placeholder="Rechercher un type de prestation"
                                    @search-change="rechercheType = $event" @open="rechercheType = ''"
                                    @select="choosePrestation(draft, $event.id === null ? '' : $event.id)">
                                    <span slot="noResult">Aucun type ne correspond à la recherche.</span>
                                    <span slot="noOptions">Choisissez d’abord une catégorie.</span>
                                </multiselect>
                            </div>
                            <div class="form-group col-md-6">
                                <label for="med-mode">Mode de rémunération</label>
                                <select id="med-mode" :value="draft.mode_remuneration" class="input-secondary-light" @change="chooseMode(draft, $event.target.value)">
                                    <option value="pourcentage">Pourcentage</option>
                                    <option value="montant_fixe">Montant fixe</option>
                                </select>
                            </div>
                            <div class="form-group col-md-6">
                                <label :for="draft.mode_remuneration === 'pourcentage' ? 'med-taux-v3' : 'med-forfait-v3'">{{ draft.mode_remuneration === 'pourcentage' ? 'Taux (%)' : 'Montant fixe (' + devise + ')' }}</label>
                                <input v-if="draft.mode_remuneration === 'pourcentage'" id="med-taux-v3" type="number" min="0" max="100" step="0.01" v-model="draft.pourcentage" class="input-secondary-light">
                                <input v-else id="med-forfait-v3" type="number" min="0" step="1" v-model="draft.montant_fixe" class="input-secondary-light">
                            </div>
                            <div v-if="draft.mode_remuneration === 'pourcentage' && selectedV3Prestation" class="form-group col-md-6">
                                <label for="med-base-v3">Base de rémunération ({{ devise }})</label>
                                <input id="med-base-v3" type="number" min="0" :max="selectedV3Prestation.montant" step="1" v-model="draft.base_remuneration" class="input-secondary-light">
                            </div>
                            <div v-else class="form-group col-md-6 med-base-unavailable">
                                <label>Base de rémunération</label>
                                <p v-if="draft.mode_remuneration === 'montant_fixe'" class="mb-0">Non utilisée pour un montant fixe.</p>
                                <p v-else class="mb-0">La base s’applique uniquement à un type précis.</p>
                            </div>
                        </div>

                        <p v-if="draftError" class="text-warning" role="status">{{ draftError }}</p>
                        <p v-else class="text-primary">
                            <template v-if="draftSchema === 3 && !selectedV3Prestation && draft.mode_remuneration === 'pourcentage'">Rémunération par acte : <strong>calculée sur le P.U. de l’acte</strong></template>
                            <template v-else>Rémunération par acte : <strong>{{ money(preview) }} {{ devise }}</strong></template>
                        </p>
                        <div class="d-flex flex-wrap justify-content-end align-items-center">
                            <div>
                                <button type="button" class="btn btn-secondary-light-o mr-2" @click="closeRule">Annuler</button>
                                <button type="button" class="btn btn-primary" :disabled="!!draftError" @click="applyRule">{{ isNewRule ? 'Ajouter' : 'Appliquer' }}</button>
                            </div>
                        </div>
                    </div>
                </div>

                <p class="small text-grey">Les modifications des règles seront sauvegardées avec le bouton Enregistrer.</p>
                <div class="text-right mt-4">
                    <button type="submit" class="btn btn-primary w-145p brder-radius" :disabled="saving || !!draft">
                        Enregistrer
                        <div v-if="saving" class="spinner-border text-white" role="status"><span class="sr-only">Loading...</span></div>
                    </button>
                </div>
            </fieldset>
        </form>
    </section>
</template>

<script>
import $ from 'jquery'
import config from '@/store/mods/parametres/config'
import {
    estRegleV3,
    erreurRegle,
    erreurRegleV3,
    libelleCategorie,
    messageErreur,
    normaliserCatalogueRemuneration,
    normaliserIdPrestation,
    remunerationActe,
    remunerationFixe
} from './remuneration-utils'

const copy = value => JSON.parse(JSON.stringify(value))

export default {
    name: 'medecin-remuneration',
    props: { userId: { type: Number, required: true } },
    data() {
        return {
            loading: false, loaded: false, saving: false, error: '', success: false,
            prestations: [], profil: { statut: 'interne', is_active: true, regles: [] },
            draft: null, editIndex: -1, migrationIntent: false, legacyRulesBeforeMigration: null,
            rechercheType: ''
        }
    },
    computed: {
        // Devise principale des paramètres (Paramètres → Établissement → Devise), jamais écrite en dur.
        devise() {
            const info = this.$store.getters['info_currency'] || {}
            return info.principal_currency || ''
        },
        isNewRule() { return this.editIndex < 0 },
        catalogueCategories() { return normaliserCatalogueRemuneration(this.prestations) },
        legacyMode() { return !this.migrationIntent && this.profil.regles.some(regle => !estRegleV3(regle)) },
        legacyAvailablePrestations() {
            // Le contrat V1 ne sait rémunérer que les consultations. Ne pas
            // mélanger les identifiants des nouvelles catégories dans son
            // sélecteur historique.
            const all = this.typesFor('consultation')
            return all.filter(prestation => !this.profil.regles.some((regle, index) => index !== this.editIndex && Number(regle.prestation_id) === Number(prestation.id)))
        },
        selectedLegacyPrestation() {
            return this.draft ? this.legacyAvailablePrestations.concat(this.typesFor('consultation')).find(prestation => Number(prestation.id) === Number(this.draft.prestation_id)) : null
        },
        selectedV3Prestation() { return this.draft && this.draftSchema === 3 ? this.typeForRule(this.draft) : null },
        draftSchema() { return this.draft && estRegleV3(this.draft) ? 3 : 1 },
        draftError() {
            if (!this.draft) return ''
            return this.draftSchema === 3
                ? erreurRegleV3(this.draft, this.categoryFor(this.draft.categorie), this.selectedV3Prestation)
                : erreurRegle(this.draft, this.selectedLegacyPrestation)
        },
        preview() {
            if (!this.draft || this.draftError) return null
            if (this.draftSchema === 1) return remunerationActe(this.draft.base_remuneration, this.draft.pourcentage)
            if (this.draft.mode_remuneration === 'montant_fixe') return remunerationFixe(this.draft.montant_fixe)
            return this.selectedV3Prestation ? remunerationActe(this.draft.base_remuneration, this.draft.pourcentage) : null
        },
        canAddV3Rule() {
            if (this.legacyMode || this.profil.regles.length >= 200) return false
            return this.catalogueCategories.some(categorie => {
                const ids = [null].concat(categorie.prestations.map(prestation => prestation.id))
                return ids.some(id => !this.ruleExists(categorie.categorie, id))
            })
        }
    },
    mounted() {
        if (!this.devise) this.$store.dispatch('getConfigInfoClinique', 'currency')
        this.load()
    },
    beforeDestroy() { this.releaseRuleDialog(false) },
    methods: {
        remuneration: remunerationActe,
        // Options du champ « Type de prestation » : « Tout », puis chaque type avec son prix ; déjà réglé = grisé.
        typesRecherches(regle) {
            if (!regle || !regle.categorie) return []
            const sansAccents = texte => String(texte).toLocaleLowerCase('fr-FR').normalize('NFD').replace(/[̀-ͯ]/g, '')
            const recherche = sansAccents(this.rechercheType.trim())
            const options = [{ cle: 'tout', id: null, libelle: 'Tout', $isDisabled: this.ruleExists(regle.categorie, null, regle) }]
                .concat(this.typesFor(regle.categorie).map(prestation => ({
                    cle: 'type-' + prestation.id, id: prestation.id,
                    libelle: prestation.libelle + ' · ' + this.money(prestation.montant) + (this.devise ? ' ' + this.devise : ''),
                    $isDisabled: this.ruleExists(regle.categorie, prestation.id, regle)
                })))
            return recherche ? options.filter(option => sansAccents(option.libelle).indexOf(recherche) !== -1) : options
        },
        typeChoisi(regle) {
            if (!regle || !regle.categorie) return null
            const id = normaliserIdPrestation(regle.prestation_id)
            const prestation = id === null ? null : this.typesFor(regle.categorie).find(item => Number(item.id) === id)
            if (id === null) return { cle: 'tout', id: null, libelle: 'Tout' }
            return prestation ? { cle: 'type-' + id, id: id,
                libelle: prestation.libelle + ' · ' + this.money(prestation.montant) + (this.devise ? ' ' + this.devise : '') } : null
        },
        money(value) { return value === null || value === undefined || Number.isNaN(Number(value)) ? '—' : Number(value).toLocaleString('fr-FR') },
        allPrestations() { return this.catalogueCategories.reduce((list, categorie) => list.concat(categorie.prestations), []) },
        categoryFor(categorie) { return this.catalogueCategories.find(item => item.categorie === categorie) || null },
        typesFor(categorie) { return (this.categoryFor(categorie) || {}).prestations || [] },
        typeForRule(regle) {
            const id = normaliserIdPrestation(regle && regle.prestation_id)
            return id === null ? null : this.typesFor(regle.categorie || regle.prestation_type).find(item => Number(item.id) === id) || null
        },
        ruleExists(categorie, prestationId, ignoredRule) {
            const normalized = normaliserIdPrestation(prestationId)
            return this.profil.regles.some((regle, index) => {
                // Le brouillon d'édition est une copie : ignorer également la
                // règle à son index d'origine, sinon son propre « Tout » ou
                // son propre type précis devient artificiellement indisponible.
                const isEditedRule = ignoredRule && this.editIndex >= 0 && index === this.editIndex
                return regle !== ignoredRule && !isEditedRule && estRegleV3(regle) &&
                    (regle.categorie || regle.prestation_type) === categorie &&
                    normaliserIdPrestation(regle.prestation_id) === normalized
            })
        },
        categoryUnavailable(categorie, ignoredRule) {
            if (!categorie || categorie.categorie === (ignoredRule && ignoredRule.categorie)) return false
            const ids = [null].concat(categorie.prestations.map(prestation => prestation.id))
            return ids.every(id => this.ruleExists(categorie.categorie, id, ignoredRule))
        },
        ruleCategoryLabel(regle) { return (this.categoryFor(regle.categorie || regle.prestation_type) || {}).libelle || libelleCategorie(regle.categorie || regle.prestation_type) },
        ruleTypeLabel(regle) { return normaliserIdPrestation(regle.prestation_id) === null ? 'Tout' : (regle.prestation_libelle || (this.typeForRule(regle) || {}).libelle || 'Prestation supprimée') },
        ruleMontantPrestation(regle) {
            if (normaliserIdPrestation(regle.prestation_id) === null) return null
            if (regle.montant_prestation !== null && regle.montant_prestation !== undefined) return regle.montant_prestation
            const prestation = this.typeForRule(regle)
            return prestation ? prestation.montant : null
        },
        ruleModeLabel(regle) { return regle.mode_remuneration === 'montant_fixe' ? 'Montant fixe' : 'Pourcentage' },
        // Colonne « Règle » : mode, valeur et base en une phrase courte.
        ruleSummary(regle) {
            if (regle.mode_remuneration === 'montant_fixe') return 'Forfait fixe'
            const taux = this.money(regle.pourcentage) + ' %'
            return normaliserIdPrestation(regle.prestation_id) === null ? taux + ' du P.U.' : taux + ' de ' + this.ruleBaseLabel(regle)
        },
        ruleValueLabel(regle) { return regle.mode_remuneration === 'montant_fixe' ? this.money(regle.montant_fixe) + ' ' + this.devise : this.money(regle.pourcentage) + ' %' },
        ruleBaseLabel(regle) { return regle.mode_remuneration === 'pourcentage' && normaliserIdPrestation(regle.prestation_id) !== null ? this.money(regle.base_remuneration) + ' ' + this.devise : '—' },
        ruleDueLabel(regle) {
            if (regle.mode_remuneration === 'montant_fixe') return this.money(regle.montant_fixe) + ' ' + this.devise
            if (normaliserIdPrestation(regle.prestation_id) === null) return 'Selon le P.U.'
            const amount = regle.remuneration_acte === null || regle.remuneration_acte === undefined
                ? remunerationActe(regle.base_remuneration, regle.pourcentage)
                : regle.remuneration_acte
            return this.money(amount) + ' ' + this.devise
        },
        async load() {
            this.loading = true
            this.error = ''
            try {
                const results = await Promise.all([config.getMedecinRemuneration(this.userId), config.getRemunerationPrestations()])
                this.profil = results[0]
                this.prestations = results[1]
                this.migrationIntent = false
                this.legacyRulesBeforeMigration = null
                this.loaded = true
            } catch (error) { this.error = messageErreur(error) }
            finally { this.loading = false }
        },
        startV3Migration() {
            if (this.saving || this.draft || !this.legacyMode) return
            this.legacyRulesBeforeMigration = copy(this.profil.regles)
            this.profil.regles = []
            this.migrationIntent = true
            this.success = false
            this.error = ''
        },
        cancelV3Migration() {
            if (this.saving || this.draft || !this.migrationIntent) return
            this.profil.regles = copy(this.legacyRulesBeforeMigration || [])
            this.legacyRulesBeforeMigration = null
            this.migrationIntent = false
            this.error = ''
        },
        editRule(index) {
            if (this.saving) return
            if (this.legacyMode) this.editLegacyRule(index)
            else this.editV3Rule(index)
        },
        editLegacyRule(index) {
            if (typeof document !== 'undefined' && !this.draft) this._ruleTrigger = document.activeElement
            this.editIndex = index
            this.success = false
            this.draft = index < 0 ? { prestation_id: '', pourcentage: '', base_remuneration: '', is_active: true } : copy(this.profil.regles[index])
            this.$nextTick(this.focusRuleDialog)
        },
        editV3Rule(index) {
            if (typeof document !== 'undefined' && !this.draft) this._ruleTrigger = document.activeElement
            this.editIndex = index
            this.success = false
            this.draft = index < 0
                ? { schema_version: 3, categorie: '', prestation_type: 'consultation', prestation_id: null, prestation_scope: 'tout', mode_remuneration: 'pourcentage', pourcentage: '', base_remuneration: null, montant_fixe: null, is_active: true }
                : copy(this.profil.regles[index])
            this.normaliseV3Draft(this.draft)
            this.$nextTick(this.focusRuleDialog)
        },
        normaliseV3Draft(regle) {
            this.$set(regle, 'schema_version', 3)
            this.$set(regle, 'categorie', regle.categorie || regle.prestation_type || '')
            this.$set(regle, 'prestation_type', regle.categorie || regle.prestation_type || 'consultation')
            this.$set(regle, 'prestation_id', normaliserIdPrestation(regle.prestation_id))
            this.$set(regle, 'prestation_scope', normaliserIdPrestation(regle.prestation_id) === null ? 'tout' : 'specifique')
            this.$set(regle, 'mode_remuneration', regle.mode_remuneration || 'pourcentage')
            this.$set(regle, 'pourcentage', regle.pourcentage === null || regle.pourcentage === undefined ? '' : String(regle.pourcentage))
            this.$set(regle, 'base_remuneration', regle.base_remuneration === null || regle.base_remuneration === undefined ? null : String(regle.base_remuneration))
            this.$set(regle, 'montant_fixe', regle.montant_fixe === null || regle.montant_fixe === undefined ? null : String(regle.montant_fixe))
        },
        focusRuleDialog() {
            if (this._isDestroyed || !this.draft || !this.$refs.ruleDialog || typeof document === 'undefined') return
            this._ruleParentModal = this.$el.closest('.modal')
            if (this._ruleParentModal) this._ruleParentModal.classList.add('med-rule-open')
            document.addEventListener('keydown', this.onRuleKeydown, true)
            document.addEventListener('focusin', this.onRuleFocus, true)
            this.$refs.ruleDialog.querySelector('select').focus()
        },
        onRuleFocus(event) {
            const dialog = this.$refs.ruleDialog
            if (this.draft && dialog && !dialog.contains(event.target)) dialog.focus()
        },
        onRuleKeydown(event) {
            if (!this.draft) return
            if (event.key === 'Escape') {
                event.preventDefault()
                event.stopImmediatePropagation()
                this.closeRule()
                return
            }
            if (event.key !== 'Tab' || !this.$refs.ruleDialog) return
            const dialog = this.$refs.ruleDialog
            const controls = Array.from(dialog.querySelectorAll('button:not(:disabled), select:not(:disabled), input:not(:disabled), [tabindex="0"]')).filter(el => el.offsetParent !== null)
            const first = controls[0]
            const last = controls[controls.length - 1]
            if (!first) { event.preventDefault(); dialog.focus(); return }
            if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
                event.preventDefault(); last.focus()
            } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog)) {
                event.preventDefault(); first.focus()
            }
        },
        releaseRuleDialog(restoreFocus = true) {
            if (typeof document === 'undefined') return
            document.removeEventListener('keydown', this.onRuleKeydown, true)
            document.removeEventListener('focusin', this.onRuleFocus, true)
            if (this._ruleParentModal) this._ruleParentModal.classList.remove('med-rule-open')
            const trigger = this._ruleTrigger
            if (restoreFocus && trigger) this.$nextTick(() => {
                if (document.contains(trigger)) trigger.focus()
            })
            this._ruleTrigger = null
            this._ruleParentModal = null
        },
        closeRule() {
            this.draft = null
            this.releaseRuleDialog()
        },
        selectLegacyPrestation() {
            this.draft.base_remuneration = this.selectedLegacyPrestation && this.selectedLegacyPrestation.montant !== null ? this.selectedLegacyPrestation.montant : ''
        },
        chooseCategory(regle, value) {
            if (this.saving) return
            const categorie = this.categoryFor(value)
            if (!categorie) return
            this.$set(regle, 'categorie', categorie.categorie)
            this.$set(regle, 'prestation_type', categorie.categorie)
            const firstAvailable = categorie.prestations.find(prestation => !this.ruleExists(categorie.categorie, prestation.id, regle))
            const type = this.ruleExists(categorie.categorie, null, regle) && firstAvailable ? firstAvailable.id : null
            this.$set(regle, 'prestation_id', type)
            this.$set(regle, 'prestation_scope', type === null ? 'tout' : 'specifique')
            const prestation = this.typesFor(categorie.categorie).find(item => item.id === type)
            this.$set(regle, 'base_remuneration', regle.mode_remuneration === 'pourcentage' && prestation ? String(prestation.montant) : null)
        },
        choosePrestation(regle, value) {
            if (this.saving) return
            const prestationId = normaliserIdPrestation(value)
            this.$set(regle, 'prestation_id', prestationId)
            this.$set(regle, 'prestation_scope', prestationId === null ? 'tout' : 'specifique')
            if (regle.mode_remuneration !== 'pourcentage') return
            const prestation = this.typeForRule(regle)
            this.$set(regle, 'base_remuneration', prestation ? String(prestation.montant) : null)
        },
        chooseMode(regle, mode) {
            if (this.saving) return
            this.$set(regle, 'mode_remuneration', mode)
            if (mode === 'montant_fixe') {
                this.$set(regle, 'pourcentage', null)
                this.$set(regle, 'base_remuneration', null)
                this.$set(regle, 'montant_fixe', regle.montant_fixe || '')
                return
            }
            this.$set(regle, 'montant_fixe', null)
            this.$set(regle, 'pourcentage', regle.pourcentage === null ? '' : regle.pourcentage)
            const prestation = this.typeForRule(regle)
            this.$set(regle, 'base_remuneration', prestation ? String(prestation.montant) : null)
        },
        applyRule() {
            if (this.draftError) return
            if (this.draftSchema === 1) this.applyLegacyRule()
            else this.applyV3Rule()
        },
        applyLegacyRule() {
            const regle = Object.assign({}, this.draft, {
                prestation_type: 'consultation', prestation_id: Number(this.draft.prestation_id),
                prestation_libelle: this.selectedLegacyPrestation.libelle,
                montant_prestation: this.selectedLegacyPrestation.montant, montant_actuel: this.selectedLegacyPrestation.montant,
                pourcentage: Number(this.draft.pourcentage), base_remuneration: Number(this.draft.base_remuneration)
            })
            if (this.editIndex < 0) this.profil.regles.push(regle)
            else this.$set(this.profil.regles, this.editIndex, regle)
            this.closeRule()
            this.error = ''
        },
        applyV3Rule() {
            const categorie = this.categoryFor(this.draft.categorie)
            const prestation = this.typeForRule(this.draft)
            const prestationId = normaliserIdPrestation(this.draft.prestation_id)
            const regle = Object.assign({}, this.draft, {
                schema_version: 3,
                categorie: categorie.categorie,
                prestation_type: categorie.categorie,
                prestation_scope: prestationId === null ? 'tout' : 'specifique',
                prestation_id: prestationId,
                prestation_libelle: prestation ? prestation.libelle : 'Tout',
                montant_prestation: prestation ? prestation.montant : null,
                montant_actuel: prestation ? prestation.montant : null,
                mode_remuneration: this.draft.mode_remuneration,
                pourcentage: this.draft.mode_remuneration === 'pourcentage' ? Number(this.draft.pourcentage) : null,
                base_remuneration: this.draft.mode_remuneration === 'pourcentage' && prestation ? Number(this.draft.base_remuneration) : null,
                montant_fixe: this.draft.mode_remuneration === 'montant_fixe' ? Number(this.draft.montant_fixe) : null,
                remuneration_acte: this.preview
            })
            if (this.editIndex < 0) this.profil.regles.push(regle)
            else this.$set(this.profil.regles, this.editIndex, regle)
            this.closeRule()
            this.error = ''
        },
        toggleRule(index) {
            if (this.saving || this.draft) return
            this.profil.regles[index].is_active = !this.profil.regles[index].is_active
            this.success = false
        },
        reactivateProfile() {
            if (this.saving || this.draft) return
            this.profil.is_active = true
            this.success = false
        },
        removeRule(index) {
            if (this.saving || this.draft) return
            this.profil.regles.splice(index, 1)
            this.success = false
        },
        legacyPayload() {
            return this.profil.regles.map(regle => ({
                id: regle.id, prestation_type: regle.prestation_type, prestation_id: regle.prestation_id,
                pourcentage: regle.pourcentage, base_remuneration: regle.base_remuneration, is_active: regle.is_active
            }))
        },
        v3Payload() {
            return this.profil.regles.map(regle => {
                const prestationId = normaliserIdPrestation(regle.prestation_id)
                return {
                    id: regle.id,
                    prestation_type: regle.categorie || regle.prestation_type,
                    prestation_id: prestationId,
                    mode_remuneration: regle.mode_remuneration,
                    pourcentage: regle.mode_remuneration === 'pourcentage' ? Number(regle.pourcentage) : null,
                    base_remuneration: regle.mode_remuneration === 'pourcentage' && prestationId !== null ? Number(regle.base_remuneration) : null,
                    montant_fixe: regle.mode_remuneration === 'montant_fixe' ? Number(regle.montant_fixe) : null,
                    is_active: regle.is_active
                }
            })
        },
        async save() {
            if (this.saving || this.draft) return
            if (this.migrationIntent && !this.profil.regles.length) {
                this.error = 'Ajoutez au moins une règle V3 avant de remplacer la configuration historique.'
                return
            }
            this.saving = true
            this.error = ''
            this.success = false
            const data = {
                statut: this.profil.statut,
                is_active: this.profil.is_active,
                regles: this.legacyMode ? this.legacyPayload() : this.v3Payload()
            }
            try {
                this.profil = await config.saveMedecinRemuneration(this.userId, data)
                this.migrationIntent = false
                this.legacyRulesBeforeMigration = null
                this.success = true
                $(this.$el.closest('.modal')).modal('hide')
            } catch (error) { this.error = messageErreur(error) }
            finally { this.saving = false }
        }
    }
}
</script>

<style scoped lang="scss">
.med-remuneration {
    fieldset { border: 0; padding: 0; min-width: 0; }
    .med-legacy-notice {
        display: flex; align-items: center; justify-content: space-between; gap: 1rem;
        margin-bottom: 1.25rem; padding: 1rem 1.1rem; border: 1px solid #d8e4ff;
        border-left: 2px solid #4776fa; border-radius: 0 10px 10px 0; background: #f7faff;
        p { color: var(--ui-text-secondary, #617394); }
    }
    .med-legacy-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: .65rem; }
    .med-migration-note { border-radius: 10px; }
    .med-rules-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin: 8px 0 14px; }
    .med-rule-overlay {
        position: fixed; inset: 0; z-index: 1060; display: flex; align-items: center;
        justify-content: center; padding: 24px; background: rgba(24, 35, 55, .35);
        backdrop-filter: blur(3px); -webkit-backdrop-filter: blur(3px);
    }
    .med-rule-editor {
        width: 920px; max-width: 100%; max-height: calc(100vh - 48px); overflow-y: auto;
        padding: 28px; border: 1px solid #cddcff; border-radius: 18px; background: white;
        box-shadow: 0 12px 40px rgba(24, 35, 55, .18);
    }
    .med-base-unavailable {
        display: flex; flex-direction: column; justify-content: flex-end;
        p { min-height: 42px; padding: .6rem .75rem; color: var(--ui-text-secondary, #7182a4); background: #f7f9fd; border-radius: 8px; }
    }
    .med-rules-table { font-size: 14px; }
    .med-rules-table th { background: #f6f9ff; padding: 12px 10px; vertical-align: middle; white-space: nowrap; }
    .med-rules-table td { padding: 14px 10px; vertical-align: middle; }
    // Tient dans la largeur de la fiche : pas de largeur minimale, le texte des cellules passe à la ligne.
    .med-rules-v3-table { width: 100%; margin-bottom: 0;
        th, td { white-space: normal; text-align: left; }
        th.text-right, td.text-right { text-align: right; }
        th.med-rule-actions, td.med-rule-actions { width: 48px; text-align: right; }
        tbody tr:hover { background: #f8faff; }
    }
    .med-rule-due { font-weight: 600; white-space: nowrap; }
    .med-rule-inactive td { color: var(--ui-text-secondary, #98a3b8); }
    .med-rule-actions { white-space: nowrap; }
    .med-rule-actions button { font-size: 14px; }
    .med-type-recherche ::v-deep .multiselect__tags { min-height: 52px; padding-top: 14px; border-radius: 12px; border-color: #c9d2e6; }
    .med-type-recherche ::v-deep .multiselect__select { height: 50px; }
    @media (max-width: 767px) {
        .med-legacy-notice { align-items: flex-start; flex-direction: column; }
        .med-legacy-actions { justify-content: flex-start; }
        .med-rule-overlay { align-items: flex-start; padding: 12px; overflow-y: auto; }
        .med-rule-editor { margin: auto 0; padding: 20px; }
    }
}
</style>

<style lang="scss">
/* Keep the popup inside Bootstrap's focus boundary, without a transformed fixed ancestor. */
.add-user.med-rule-open > .modal-dialog { transform: none !important; }
</style>
